import { memo, useReducer, useEffect, useCallback, useMemo, useRef } from 'react';
import '../styles/payment-submodal.css';

function makeParticles(count, fields) {
  return Array.from({ length: count }, () => {
    const obj = {};
    for (const [key, fn] of Object.entries(fields)) obj[key] = fn();
    return obj;
  });
}

const GLASS_ORBS = makeParticles(15, {
  x: () => `${10 + Math.random() * 80}%`,
  y: () => `${10 + Math.random() * 80}%`,
  size: () => `${20 + Math.random() * 40}px`,
  duration: () => `${8 + Math.random() * 10}s`,
  delay: () => `${Math.random() * 5}s`,
  hue: () => `${Math.floor(Math.random() * 360)}`,
});

const QUANTUM_PARTICLES = makeParticles(50, {
  x: () => `${Math.random() * 100}%`,
  y: () => `${Math.random() * 100}%`,
  duration: () => `${3 + Math.random() * 5}s`,
  delay: () => `${Math.random() * 4}s`,
  size: () => `${2 + Math.random() * 4}px`,
  color: () => ['#ffd700', '#00f0ff', '#ff00ff', '#00ff88', '#ff6b6b'][Math.floor(Math.random() * 5)],
});

const SPARKLES = makeParticles(20, {
  x: () => `${Math.random() * 100}%`,
  y: () => `${Math.random() * 100}%`,
  delay: () => `${Math.random() * 4}s`,
  duration: () => `${2 + Math.random() * 3}s`,
});

const CURRENCY_SYMBOLS = { EUR: '€', USD: '$', GBP: '£', JPY: '¥', CAD: '$', AUD: '$', CHF: 'Fr' };

const OUTER_DASH = 2 * Math.PI * 80;
const INNER_DASH = 2 * Math.PI * 65;

const CONFETTI_COLORS = ['#ffd700', '#4caf50', '#2196f3', '#e91e63', '#9c27b0'];
const CONFETTI = makeParticles(30, {
  x: () => `${Math.random() * 100}%`,
  delay: () => `${(Math.random() * 0.5).toFixed(3)}s`,
  color: () => CONFETTI_COLORS[Math.floor(Math.random() * 5)],
});

const INITIAL_STATE = {
  step: 'confirm',
  progress: 0,
  agreeToTerms: false,
  termsOpen: false,
  paymentError: null,
  confirming: false,
  txId: null,
  txDate: null,
};

function reducer(state, action) {
  switch (action.type) {
    case 'RESET':
      return { ...INITIAL_STATE };
    case 'TOGGLE_TERMS':
      return { ...state, termsOpen: !state.termsOpen, agreeToTerms: action.agree ?? state.agreeToTerms };
    case 'CLOSE_TERMS':
      return { ...state, termsOpen: false };
    case 'SET_AGREE':
      return { ...state, agreeToTerms: action.value };
    case 'CONFIRMING_START':
      return { ...state, confirming: true, paymentError: null };
    case 'CONFIRMING_ERROR':
      return { ...state, confirming: false, paymentError: action.payload };
    case 'START_AUTHORIZING':
      return { ...state, confirming: false, step: 'authorizing', progress: 0 };
    case 'SET_PROGRESS':
      return { ...state, progress: action.value };
    case 'SUCCESS':
      return {
        ...state,
        step: 'success',
        progress: 100,
        txId: Math.random().toString(36).substring(2, 10).toUpperCase(),
        txDate: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      };
    default:
      return state;
  }
}

function getProgressLabel(p) {
  if (p < 25) return 'Verifying your request';
  if (p < 50) return 'Encrypting your smartcontract';
  if (p < 75) return 'Funding your smartcontract';
  return 'Confirming contract';
}


function formatAmount(amount) {
  return parseFloat(amount || '100').toFixed(2);
}

const TermsModal = memo(function TermsModal({ onClose }) {
  const stopProp = useCallback((e) => e.stopPropagation(), []);
  return (
    <div className="terms-modal-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label="Terms and Conditions">
      <div className="terms-modal-container" onClick={stopProp}>
        <button className="terms-modal-close" onClick={onClose} aria-label="Close terms">
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
        <div className="terms-modal-header">
          <div className="terms-logo-wrapper">
            <img src="/icon.png" alt="Logo" className="terms-logo" />
          </div>
          <h2>Terms &amp; Conditions</h2>
          <p>Smart Contract Service Agreement</p>
        </div>
        <div className="terms-modal-content">
          <section className="terms-section">
            <h3>1. Service Agreement</h3>
            <p>By using our Smart Contract service, you agree to these Terms and Conditions. We provide digital asset transaction services through decentralized blockchain networks.</p>
          </section>
          <section className="terms-section">
            <h3>2. User Responsibilities</h3>
            <p>You are responsible for:</p>
            <ul>
              <li>Maintaining the confidentiality of your account credentials</li>
              <li>Providing accurate and complete information</li>
              <li>Complying with all applicable laws and regulations</li>
              <li>Using the service for lawful purposes only</li>
            </ul>
          </section>
          <section className="terms-section">
            <h3>3. Payment Terms</h3>
            <p>All payments are processed immediately upon authorization. Transactions are final and irreversible. We do not process refunds for authorized payments unless required by law.</p>
          </section>
          <section className="terms-section">
            <h3>4. Service Availability</h3>
            <p>While we strive to maintain continuous service, we do not guarantee uninterrupted availability. Smart Contract execution depends on blockchain network conditions.</p>
          </section>
          <section className="terms-section">
            <h3>5. Limitation of Liability</h3>
            <p>We are not liable for indirect, incidental, special, consequential, or punitive damages arising from your use of our service. Our total liability is limited to the amount paid in the transaction.</p>
          </section>
          <section className="terms-section">
            <h3>6. Intellectual Property</h3>
            <p>All content, including logos, designs, and text, is our exclusive property or licensed to us. Reproduction without permission is prohibited.</p>
          </section>
          <section className="terms-section">
            <h3>7. Data Privacy</h3>
            <p>We collect and process personal data in accordance with applicable privacy laws. Your information is encrypted and protected with industry-standard security measures.</p>
          </section>
          <section className="terms-section">
            <h3>8. User Conduct</h3>
            <p>You agree not to:</p>
            <ul>
              <li>Engage in fraudulent or illegal activities</li>
              <li>Attempt to breach security measures</li>
              <li>Harass or abuse our team members</li>
              <li>Interfere with service operations</li>
            </ul>
          </section>
          <section className="terms-section">
            <h3>9. Compliance</h3>
            <p>Users must comply with all applicable laws, regulations, and sanctions programs. We reserve the right to deny service to users from restricted jurisdictions.</p>
          </section>
          <section className="terms-section">
            <h3>10. Modification of Terms</h3>
            <p>We may modify these terms at any time. Continued use of our service constitutes acceptance of updated terms. Changes are effective immediately upon publication.</p>
          </section>
          <section className="terms-section">
            <h3>11. Dispute Resolution</h3>
            <p>Any disputes shall be resolved through binding arbitration in accordance with applicable law. You waive the right to pursue claims in court or jury trial.</p>
          </section>
          <section className="terms-section">
            <h3>12. Contact Information</h3>
            <p>For questions or concerns regarding these terms, please contact our support team at support@sodl.com.</p>
          </section>
          <section className="terms-section terms-section-last">
            <p className="terms-footer">
              <strong>Last Updated:</strong> January 2026<br />
              By checking the agreement box, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
            </p>
          </section>
        </div>
        <div className="terms-modal-footer">
          <button className="terms-btn-close" onClick={onClose}>I Understand</button>
        </div>
      </div>
    </div>
  );
});

const ConfirmStep = memo(function ConfirmStep({
  paymentData, agreeToTerms, paymentError, confirming,
  onClose, onAgreeChange, onOpenTerms, onConfirmPay,
}) {
  const amount = useMemo(() => formatAmount(paymentData?.amount), [paymentData?.amount]);
  const currency = paymentData?.currency || 'EUR';
  const currencySymbol = CURRENCY_SYMBOLS[currency] ?? currency;

  return (
    <div className="submodal-content confirm-step">
      <button className="submodal-close" onClick={onClose} aria-label="Close">
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>

      <div className="submodal-header">
        <div className="logo-wrapper">
          <div className="logo-ring" />
          <div className="logo-box">
            <img src="/icon.png" alt="Logo" className="logo-image" />
          </div>
        </div>
        <h2>Confirm Payment</h2>
        <p>This merchant accepts payments in</p>
        <p>Smart Contracts</p>
      </div>

      <div className={`amount-card${!agreeToTerms ? ' section-blurred' : ''}`}>
        <div className="amount-card-bg" />
        <div className="amount-label">AMOUNT TO PAY</div>
        <div className="amount-row">
          <span className="currency-sign">{currencySymbol}</span>
          <span className="amount-number">{amount}</span>
          <span className="currency-code">{currency}</span>
        </div>
      </div>

      <div className={`terms-agreement${!agreeToTerms ? ' terms-highlight' : ''}`}>
        <label className="checkbox-container">
          <input
            type="checkbox"
            checked={agreeToTerms}
            onChange={(e) => onAgreeChange(e.target.checked)}
            className="checkbox-input"
          />
          <span className="checkbox-label">
            I agree to purchase Smart Contract service and to the{' '}
            <button
              type="button"
              className="terms-link"
              onClick={onOpenTerms}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
            >
              T&amp;Cs
            </button>
          </span>
        </label>
      </div>

      <div className={`privacy-policy-section${!agreeToTerms ? ' section-blurred' : ''}`}>
        <span className="privacy-policy-label">Privacy Policy</span>
      </div>

      {paymentError && (
        <p className="payment-error-msg" role="alert">{paymentError}</p>
      )}

      <div className={`submodal-actions${!agreeToTerms ? ' section-blurred' : ''}`}>
        <button
          className={`btn-primary${confirming ? ' btn-loading' : ''}`}
          onClick={onConfirmPay}
          disabled={!agreeToTerms || confirming}
          aria-busy={confirming}
        >
          {confirming ? (
            <span>Processing…</span>
          ) : (
            <>
              <span>Pay {currencySymbol}{amount}</span>
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </>
          )}
        </button>
      </div>
    </div>
  );
});

const AuthorizingStep = memo(function AuthorizingStep({ progress }) {
  const label = useMemo(() => getProgressLabel(progress), [progress]);
  const outerOffset = OUTER_DASH * (1 - progress / 100);
  const innerOffset = INNER_DASH * (1 - Math.min(progress * 1.3, 100) / 100);

  return (
    <div className="submodal-content authorizing-step">
      <div className="aurora-bg">
        <div className="aurora-layer aurora-1" />
        <div className="aurora-layer aurora-2" />
        <div className="aurora-layer aurora-3" />
      </div>
      <div className="mesh-gradient" />
      <div className="holo-grid" />

      <div className="glass-orbs" aria-hidden="true">
        {GLASS_ORBS.map((o, i) => (
          <div key={i} className="glass-orb" style={{ '--x': o.x, '--y': o.y, '--size': o.size, '--duration': o.duration, '--delay': o.delay, '--hue': o.hue }} />
        ))}
      </div>

      <div className="dna-helix" aria-hidden="true">
        {Array.from({ length: 24 }, (_, i) => (
          <div key={i} className={`dna-particle ${i % 2 === 0 ? 'strand-a' : 'strand-b'}`} style={{ '--index': i, '--delay': `${i * 0.15}s` }} />
        ))}
      </div>

      <div className="quantum-field" aria-hidden="true">
        {QUANTUM_PARTICLES.map((q, i) => (
          <div key={i} className="quantum-particle" style={{ '--x': q.x, '--y': q.y, '--duration': q.duration, '--delay': q.delay, '--size': q.size, '--color': q.color }} />
        ))}
      </div>

      <div className="tesseract-container" aria-hidden="true">
        <div className="tesseract">
          <div className="tesseract-inner">
            {['front', 'back', 'right', 'left', 'top', 'bottom'].map(f => <div key={f} className={`tess-face tess-${f}`} />)}
          </div>
          <div className="tesseract-outer">
            {['front', 'back', 'right', 'left', 'top', 'bottom'].map(f => <div key={f} className={`tess-face tess-${f}`} />)}
          </div>
          <div className="tesseract-edges">
            {Array.from({ length: 8 }, (_, i) => <div key={i} className={`tess-edge edge-${i}`} />)}
          </div>
        </div>
      </div>

      <div className="glass-core">
        <div className="rotating-rings" aria-hidden="true">
          <div className="ring-3d ring-x" />
          <div className="ring-3d ring-y" />
          <div className="ring-3d ring-z" />
        </div>

        <div className="glass-progress-container">
          <svg className="progress-svg" viewBox="0 0 200 200" aria-hidden="true">
            <defs>
              <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#ffd700" />
                <stop offset="50%" stopColor="#ff9500" />
                <stop offset="100%" stopColor="#ffd700" />
              </linearGradient>
              <linearGradient id="cyanGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#00f0ff" />
                <stop offset="50%" stopColor="#00a0ff" />
                <stop offset="100%" stopColor="#00f0ff" />
              </linearGradient>
              <filter id="glowFilter">
                <feGaussianBlur stdDeviation="4" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>
            <circle className="track-ring" cx="100" cy="100" r="80" />
            <circle className="track-ring-inner" cx="100" cy="100" r="65" />
            <circle className="progress-ring-main" cx="100" cy="100" r="80" style={{ strokeDasharray: OUTER_DASH, strokeDashoffset: outerOffset }} />
            <circle className="progress-ring-secondary" cx="100" cy="100" r="65" style={{ strokeDasharray: INNER_DASH, strokeDashoffset: innerOffset }} />
          </svg>

          <div className="core-content">
            <div className="ice-cream-factory">
              <div className="scattered-logos" aria-hidden="true">
                <img src="/icon.png" alt="" className="scatter-logo logo-tl-1" />
                <img src="/icon.png" alt="" className="scatter-logo logo-tl-2" />
                <img src="/icon.png" alt="" className="scatter-logo logo-tr-1" />
                <img src="/icon.png" alt="" className="scatter-logo logo-tr-2" />
                <img src="/icon.png" alt="" className="scatter-logo logo-bl-1" />
                <img src="/icon.png" alt="" className="scatter-logo logo-bl-2" />
                <img src="/icon.png" alt="" className="scatter-logo logo-bl-3" />
                <img src="/icon.png" alt="" className="scatter-logo logo-br-1" />
                <img src="/icon.png" alt="" className="scatter-logo logo-br-2" />
                <img src="/icon.png" alt="" className="scatter-logo logo-br-3" />
              </div>

              <div className="icecream-process">
                <div className="orbital-system" aria-hidden="true">
                  <div className="orbit orbit-1" /><div className="orbit orbit-2" /><div className="orbit orbit-3" />
                  {Array.from({ length: 6 }, (_, i) => (
                    <div key={i} className="orbit-particle" style={{ '--i': i, '--delay': `${i * 0.8}s` }} />
                  ))}
                </div>

                <div className="center-display">
                  <div className="holographic-ring" /><div className="holographic-ring ring-inner" />
                  <div className="ice-cream-result" style={{ '--progress': progress }}>
                    <div className={`result-cone${progress >= 25 ? ' visible' : ''}`}>
                      <div className="cone-texture" /><div className="cone-shine" />
                    </div>
                    <div className={`result-scoop scoop-cyan${progress >= 50 ? ' visible' : ''}`}>
                      <div className="scoop-shine" />
                    </div>
                    <div className={`result-scoop scoop-gold${progress >= 75 ? ' visible' : ''}`}>
                      <div className="scoop-shine" />
                    </div>
                    <div className={`result-scoop scoop-pink${progress >= 95 ? ' visible' : ''}`}>
                      <div className="scoop-shine" />
                      <div className="topping-sprinkles">
                        {Array.from({ length: 8 }, (_, i) => <span key={i} className="sprinkle-dot" style={{ '--i': i }} />)}
                      </div>
                      <div className="cherry-top"><div className="cherry-body" /><div className="cherry-stem" /></div>
                    </div>
                  </div>
                  <div className="energy-field" aria-hidden="true">
                    {Array.from({ length: 12 }, (_, i) => <div key={i} className="energy-particle" style={{ '--i': i }} />)}
                  </div>
                </div>

                <div className="process-nodes" aria-hidden="true">
                  {[
                    { cls: 'node-1', stream: 'stream-1', label: 'PREPARE', activeAt: 0, doneAt: 33, progress: Math.min(progress / 33 * 100, 100), icon: <svg viewBox="0 0 24 24" fill="none"><path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" /><path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" /><path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" /></svg> },
                    { cls: 'node-2', stream: 'stream-2', label: 'MIX', activeAt: 33, doneAt: 66, progress: progress >= 33 ? Math.min((progress - 33) / 33 * 100, 100) : 0, icon: <svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" /><path d="M12 1V3M12 21V23M4.22 4.22L5.64 5.64M18.36 18.36L19.78 19.78M1 12H3M21 12H23M4.22 19.78L5.64 18.36M18.36 5.64L19.78 4.22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg> },
                    { cls: 'node-4', stream: 'stream-4', label: 'SERVE', activeAt: 66, doneAt: 100, progress: progress >= 66 ? Math.min((progress - 66) / 34 * 100, 100) : 0, icon: <svg viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg> },
                  ].map(({ cls, stream, label, activeAt, doneAt, progress: np, icon }) => (
                    <div key={cls} className={`process-node ${cls}${progress >= activeAt ? ' active' : ''}${progress >= doneAt ? ' done' : ''}`}>
                      <div className="node-connector" />
                      <div className="node-body">
                        <div className="node-glow" />
                        <div className="node-icon">{icon}</div>
                        <span className="node-label">{label}</span>
                        <div className="node-progress" style={{ '--p': np }} />
                      </div>
                      <div className={`data-stream ${stream}`}>
                        {Array.from({ length: 5 }, (_, i) => <span key={i} className="data-bit" style={{ '--i': i }} />)}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="ambient-sparkles" aria-hidden="true">
                  {SPARKLES.map((s, i) => (
                    <div key={i} className="ambient-spark" style={{ '--x': s.x, '--y': s.y, '--delay': s.delay, '--duration': s.duration }} />
                  ))}
                </div>
              </div>
            </div>

            <div className="percentage-display" aria-label={`${Math.round(progress)}% complete`}>
              <span className="percentage-value">{Math.round(progress)}</span>
              <span className="percentage-sign">%</span>
            </div>
          </div>
        </div>
      </div>

      <div className="glass-status" role="status" aria-live="polite">
        <div className="status-beacon" />
        <h2 className="neon-title">
          <span className="text-shimmer" data-text="PROCESSING">PROCESSING</span>
        </h2>
        <p className="cyber-subtitle">
          {label}<span className="blink-cursor" />
        </p>
      </div>

      <div className="glass-timeline" aria-hidden="true">
        <div className="glass-track">
          <div className="glass-track-fill" style={{ width: `${progress}%` }} />
          <div className="track-shimmer" />
        </div>
        <div className="glass-nodes">
          {[
            { label: 'VERIFY', threshold: 0, done: 25 },
            { label: 'ENCRYPT', threshold: 25, done: 50 },
            { label: 'TRANSFER', threshold: 50, done: 75 },
            { label: 'CONFIRM', threshold: 75, done: 100 },
          ].map(({ label, threshold, done }, idx, arr) => (
            <div key={label} className={`glass-node${progress >= threshold ? ' active' : ''}${progress >= done ? ' completed' : ''}`}>
              <div className="node-glow" />
              <div className="node-square">
                <div className="node-glass-bg" />
                <div className="node-border" />
                <div className="node-inner">
                  {progress >= done ? (
                    <svg viewBox="0 0 24 24"><path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  ) : (
                    <div className="node-icon">
                      {idx === 0 && <svg viewBox="0 0 24 24" fill="none"><path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" /></svg>}
                      {idx === 1 && <svg viewBox="0 0 24 24" fill="none"><rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="1.5" /><path d="M7 11V7a5 5 0 0110 0v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>}
                      {idx === 2 && <svg viewBox="0 0 24 24" fill="none"><path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                      {idx === 3 && <svg viewBox="0 0 24 24" fill="none"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                    </div>
                  )}
                </div>
                <div className="node-particles">
                  {Array.from({ length: 6 }, (_, i) => <div key={i} className="n-particle" style={{ '--i': i }} />)}
                </div>
              </div>
              <span className="node-text">{label}</span>
              {idx < arr.length - 1 && <div className="node-connector" />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});

const SuccessStep = memo(function SuccessStep({ paymentData, txId, txDate, onDone }) {
  const amount = useMemo(() => formatAmount(paymentData?.amount), [paymentData?.amount]);
  const currencySymbol = CURRENCY_SYMBOLS[paymentData?.currency] ?? (paymentData?.currency || '€');

  return (
    <div className="submodal-content success-step">
      <div className="confetti" aria-hidden="true">
        {CONFETTI.map((c, i) => (
          <div key={i} className="confetti-piece" style={{ '--x': c.x, '--delay': c.delay, '--color': c.color }} />
        ))}
      </div>

      <div className="success-icon-wrapper">
        <div className="success-pulse" />
        <div className="success-icon">
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path className="check-path" d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>

      <h2 className="success-title">Payment Successful!</h2>
      <p className="success-subtitle">Your payment for smart contracts service has been completed</p>

      <div className="success-amount">
        <span className="currency">{currencySymbol}</span>
        <span className="value">{amount}</span>
      </div>

      <div className="transaction-card">
        <div className="tx-row"><span className="tx-label">Transaction ID</span><span className="tx-value">#{txId}</span></div>
        <div className="tx-row"><span className="tx-label">Date</span><span className="tx-value">{txDate}</span></div>
        <div className="tx-row"><span className="tx-label">Status</span><span className="tx-value status-complete">Complete</span></div>
      </div>

      <button className="btn-success" onClick={onDone}>
        <span>Continue</span>
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </div>
  );
});

function PaymentSubModal({ isOpen, onClose, onStripeConfirm, paymentData }) {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!isOpen) dispatch({ type: 'RESET' });
  }, [isOpen]);

  const progressRef = useRef(0);
  useEffect(() => {
    if (state.step !== 'authorizing') {
      progressRef.current = 0;
      return;
    }
    progressRef.current = 0;
    clearInterval(intervalRef.current);

    intervalRef.current = setInterval(() => {
      progressRef.current += 1;
      if (progressRef.current >= 100) {
        clearInterval(intervalRef.current);
        dispatch({ type: 'SET_PROGRESS', value: 100 });
        setTimeout(() => dispatch({ type: 'SUCCESS' }), 500);
      } else {
        dispatch({ type: 'SET_PROGRESS', value: progressRef.current });
      }
    }, 100);

    return () => clearInterval(intervalRef.current);
  }, [state.step]);

  const handleClose = useCallback(() => onClose(), [onClose]);

  const handleAgreeChange = useCallback((val) => {
    dispatch({ type: 'SET_AGREE', value: val });
  }, []);

  const handleOpenTerms = useCallback(() => {
    dispatch({ type: 'TOGGLE_TERMS', agree: true });
  }, []);

  const handleCloseTerms = useCallback(() => {
    dispatch({ type: 'CLOSE_TERMS' });
  }, []);

  const handleConfirmPay = useCallback(async () => {
    dispatch({ type: 'CONFIRMING_START' });

    if (onStripeConfirm) {
      const onError = (msg) => dispatch({ type: 'CONFIRMING_ERROR', payload: msg });
      const ok = await onStripeConfirm(onError);
      if (!ok) return; // onError already reset confirming with the error message
    }

    dispatch({ type: 'START_AUTHORIZING' });
  }, [onStripeConfirm]);

  const handleDone = useCallback(() => {
    const uid = new URLSearchParams(window.location.search).get('uid') || '';
    window.location.href = `${process.env.REACT_APP_API_BASE_URL}/goto.php?uid=${uid}`;
  }, []);

  const stopProp = useCallback((e) => e.stopPropagation(), []);

  if (!isOpen) return null;

  return (
    <div className="submodal-overlay" onClick={handleClose} role="dialog" aria-modal="true" aria-label="Payment confirmation">
      <div className="submodal-container" onClick={stopProp}>
        <div className="submodal-glow" aria-hidden="true" />
        <div className="submodal-border-glow" aria-hidden="true" />

        {state.step === 'confirm' && (
          <ConfirmStep
            paymentData={paymentData}
            agreeToTerms={state.agreeToTerms}
            paymentError={state.paymentError}
            confirming={state.confirming}
            onClose={handleClose}
            onAgreeChange={handleAgreeChange}
            onOpenTerms={handleOpenTerms}
            onConfirmPay={handleConfirmPay}
          />
        )}

        {state.step === 'authorizing' && (
          <AuthorizingStep progress={state.progress} />
        )}

        {state.step === 'success' && (
          <SuccessStep
            paymentData={paymentData}
            txId={state.txId}
            txDate={state.txDate}
            onDone={handleDone}
          />
        )}

        {state.termsOpen && <TermsModal onClose={handleCloseTerms} />}
      </div>
    </div>
  );
}

export default memo(PaymentSubModal);
